import React, { useState, useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';

const LearningObjectives = () => {
  const { showSuccess, showError } = useApp();
  const [objectives, setObjectives] = useState([]);
  const [filteredObjectives, setFilteredObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [formData, setFormData] = useState({
    level: '',
    code: '',
    description: '',
    type: ''
  });

  // Niveles disponibles
  const levels = [
    '1° Básico',
    '2° Básico',
    '3° Básico',
    '4° Básico',
    '5° Básico',
    '6° Básico',
    '7° Básico',
    '8° Básico',
    '1° Medio',
    '2° Medio',
    '3° Medio',
    '4° Medio'
  ];

  // Tipos de OA disponibles
  const types = [
    { id: 'mineduc', name: 'Mineduc' },
    { id: 'propio', name: 'Propio' }
  ];

  useEffect(() => {
    loadObjectives();
  }, []);

  useEffect(() => {
    filterObjectives();
  }, [searchText, selectedLevel, selectedType, objectives]);

  const loadObjectives = async () => {
    try {
      setLoading(true);
      // Mock data - aquí se haría la llamada real a la API
      const mockObjectives = [
        { id: 1, level: '1° Básico', code: 'OA1', description: 'Reconocer y aplicar los conceptos de adición y sustracción en situaciones cotidianas', type: 'mineduc' },
        { id: 2, level: '1° Básico', code: 'OA2', description: 'Identificar y describir figuras geométricas básicas en el entorno', type: 'mineduc' },
        { id: 3, level: '1° Básico', code: 'OA3', description: 'Comparar y ordenar números hasta el 20', type: 'mineduc' },
        { id: 4, level: '2° Básico', code: 'OA1', description: 'Resolver problemas de adición y sustracción con números hasta 100', type: 'mineduc' },
        { id: 5, level: '2° Básico', code: 'OA2', description: 'Comprender el concepto de multiplicación como adición repetida', type: 'propio' },
        { id: 6, level: '3° Básico', code: 'OA1', description: 'Aplicar estrategias de cálculo mental para resolver operaciones', type: 'mineduc' },
        { id: 7, level: '3° Básico', code: 'OA2', description: 'Identificar y describir patrones numéricos y geométricos', type: 'propio' },
        { id: 8, level: '4° Básico', code: 'OA1', description: 'Resolver problemas que involucren las cuatro operaciones básicas', type: 'mineduc' },
        { id: 9, level: '5° Básico', code: 'OA1', description: 'Comprender y aplicar fracciones en contextos diversos', type: 'propio' },
        { id: 10, level: '6° Básico', code: 'OA1', description: 'Analizar y representar datos mediante gráficos estadísticos', type: 'mineduc' }
      ];
      setObjectives(mockObjectives);
      setFilteredObjectives(mockObjectives);
    } catch (error) {
      showError('Error al cargar los objetivos de aprendizaje');
    } finally {
      setLoading(false);
    }
  };

  const filterObjectives = () => {
    let filtered = [...objectives];

    // Filtrar por nivel
    if (selectedLevel) {
      filtered = filtered.filter(obj => obj.level === selectedLevel);
    }

    // Filtrar por tipo
    if (selectedType) {
      filtered = filtered.filter(obj => obj.type === selectedType);
    }

    // Filtrar por texto de búsqueda
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(obj =>
        obj.level.toLowerCase().includes(search) ||
        obj.code.toLowerCase().includes(search) ||
        obj.description.toLowerCase().includes(search)
      );
    }

    setFilteredObjectives(filtered);
  };

  const handleOpenModal = (objective = null) => {
    if (objective) {
      setEditingObjective(objective);
      setFormData({
        level: objective.level,
        code: objective.code,
        description: objective.description,
        type: objective.type
      });
    } else {
      setEditingObjective(null);
      setFormData({
        level: '',
        code: '',
        description: '',
        type: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingObjective(null);
    setFormData({
      level: '',
      code: '',
      description: '',
      type: ''
    });
  };

  const handleSaveObjective = async () => {
    if (!formData.level || !formData.code || !formData.description.trim() || !formData.type) {
      showError('Todos los campos son requeridos');
      return;
    }

    try {
      // Aquí se enviaría a la API
      if (editingObjective) {
        showSuccess('Objetivo de aprendizaje actualizado correctamente');
      } else {
        showSuccess('Objetivo de aprendizaje creado correctamente');
      }

      handleCloseModal();
      loadObjectives();
    } catch (error) {
      showError('Error al guardar el objetivo de aprendizaje');
    }
  };

  const handleDeleteObjective = async (objectiveId) => {
    if (window.confirm('¿Estás seguro de eliminar este objetivo de aprendizaje?')) {
      try {
        // Aquí se enviaría a la API
        showSuccess('Objetivo de aprendizaje eliminado correctamente');
        loadObjectives();
      } catch (error) {
        showError('Error al eliminar el objetivo de aprendizaje');
      }
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedLevel('');
    setSelectedType('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Objetivos de Aprendizaje
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Gestiona los objetivos de aprendizaje por nivel educativo
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nuevo Objetivo</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Búsqueda por texto */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Buscar por nivel, código o descripción..."
              />
              <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Selector de nivel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nivel
            </label>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Todos los niveles</option>
              {levels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          {/* Selector de tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Todos los tipos</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Botón para limpiar filtros */}
        {(searchText || selectedLevel || selectedType) && (
          <div className="mt-4">
            <button
              onClick={handleClearFilters}
              className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Limpiar filtros</span>
            </button>
          </div>
        )}
      </div>

      {/* Tabla de objetivos */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '12%' }}>
                  Nivel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '8%' }}>
                  Código
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '60%' }}>
                  Descripción
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredObjectives.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-gray-500">No se encontraron objetivos de aprendizaje</p>
                    <p className="text-sm text-gray-400 mt-1">
                      {searchText || selectedLevel ? 'Intenta cambiar los filtros' : 'Comienza agregando un nuevo objetivo'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredObjectives.map((objective) => (
                  <tr key={objective.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {objective.level}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {objective.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        objective.type === 'mineduc'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {types.find(t => t.id === objective.type)?.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {objective.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleOpenModal(objective)}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Editar"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteObjective(objective.id)}
                          className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                          title="Eliminar"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer con total de resultados */}
        {filteredObjectives.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Mostrando <span className="font-medium">{filteredObjectives.length}</span> de <span className="font-medium">{objectives.length}</span> objetivos
            </p>
          </div>
        )}
      </div>

      {/* Modal para crear/editar objetivo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                {editingObjective ? 'Editar Objetivo de Aprendizaje' : 'Nuevo Objetivo de Aprendizaje'}
              </h3>
            </div>

            <div className="p-6 space-y-6">
              {/* Nivel y Tipo en la misma fila */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nivel */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nivel *
                  </label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar nivel...</option>
                    {levels.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar tipo...</option>
                    {types.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Código */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ej: OA1"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe el objetivo de aprendizaje..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-4">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveObjective}
                disabled={!formData.level || !formData.code || !formData.description.trim() || !formData.type}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingObjective ? 'Guardar Cambios' : 'Crear Objetivo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningObjectives;
